import React, {useState, useEffect, useCallback, useRef} from 'react';
import styled from 'styled-components';
import ReactFlow, {
    Background, Controls, MiniMap,
    useNodesState, useEdgesState,
    MarkerType, Handle, Position, Node, Edge,
    addEdge, Connection, EdgeProps, getSmoothStepPath, BaseEdge, EdgeLabelRenderer,
    useReactFlow, ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import {useParams, useNavigate, useLocation, Routes, Route} from 'react-router-dom';
import Input from '../components/common/Input';

import {
    getProjectById,
    getProjectCards,
    updateCard,
    deleteProject,
    Project,
    Card,
    getMyIdleCards,
    assignCardsToProject,
    deleteCard,
    getCardById,
    updateProject,
    ProjectStatus,
    unassignCardFromProject,
    searchUsers,
    updateCardEdges,
    ProjectCardNodeData
} from '../api';
import useAuth from '../hooks/useAuth';
import CardDetailModal from '../components/board/CardDetailModal';
import Button from '../components/common/Button';
import Modal from "../components/common/Modal";
import ResponsiveHeader from "../components/common/ResponsiveHeader";
import {User} from "../store/userStore";

// --- Styled Components ---
const ContextMenu = styled.div<{ x: number; y: number }>`
  position: fixed;
  top: ${props => props.y}px;
  left: ${props => props.x}px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.5);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  min-width: 150px;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: var(--text-bright);
  padding: 0.75rem 1rem;
  text-align: left;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.9rem;
  &:hover { background: var(--bg-input); }
`;

const MemberList = styled.div` margin-top: 1rem; `;
const MemberItem = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.5rem; background: var(--bg-input); border-radius: 4px; margin-bottom: 0.5rem;
`;

const DiagramArea = styled.div` flex: 1; height: 100%; position: relative; display: flex; overflow: hidden; `;

// ProjectDetailPage.tsx

// 1. 부모 컨테이너는 꽉 차게 유지
const SidebarContainer = styled.div<{ isOpen: boolean }>`
  width: ${props => props.isOpen ? '350px' : '0'};
  background: var(--bg-card);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease-in-out;
  overflow: hidden; // 넘치는 자식은 숨기되, 
  flex-shrink: 0;
  position: relative;
  height: 100%; // 높이를 명확히 지정합니다
  z-index: 15;

  @media (max-width: 768px) {
    position: absolute;
    height: 100%;
    width: ${props => props.isOpen ? '100%' : '0'};
  }
`;

// 2. 실제 컨텐츠 영역에 스크롤을 부여합니다
const SidebarContent = styled.div`
  padding: 1.5rem;
  width: 350px;
  flex: 1; // 남은 공간을 다 차지하게 하고
  overflow-y: auto; // 세로 방향으로 스크롤이 생기게 합니다

  // 스크롤바 디자인 (다른 페이지와 통일감 부여)
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--text-dark);
    border-radius: 10px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Container = styled.div` display: flex; flex-direction: column; height: 100vh; width: 100vw; overflow: hidden; background: var(--bg-body); `;

const AddTaskModalContent = styled.div` padding: 1rem; display: flex; flex-direction: column; height: 60vh; `;
const TaskSelectRequest = styled.div` flex: 1; overflow-y: auto; border: 1px solid var(--border-color); padding: 0.5rem; margin-bottom: 1rem; background: var(--bg-input); `;
const TaskItem = styled.div` display: flex; gap: 0.5rem; padding: 0.5rem; border-bottom: 1px solid var(--border-color); align-items: center; color: var(--text-bright); `;

// const HeaderActions = styled.div` display: flex; align-items: center; gap: 1rem; `;

const SidebarToggleBtn = styled.button`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1100;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  color: var(--text-bright);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  &:hover { background: var(--bg-input); }
`;

const SidebarHeader = styled.div` margin-bottom: 2rem; border-bottom: 1px solid var(--border-color); padding-bottom: 1rem; `;
const InputGroup = styled.div` margin-bottom: 1.5rem; `;
const Label = styled.label` display: block; color: var(--text-normal); font-size: 0.85rem; margin-bottom: 0.5rem; `;
const StyledInput = styled.input`
  width: 100%; background: var(--bg-input); border: 1px solid var(--border-color);
  color: var(--text-bright); padding: 0.5rem; border-radius: 4px;
`;
const StyledTextarea = styled.textarea`
  width: 100%; background: var(--bg-input); border: 1px solid var(--border-color);
  color: var(--text-bright); padding: 0.5rem; border-radius: 4px; resize: vertical; min-height: 100px;
`;

const CardNodeWrapper = styled.div<{ styles: any; data: Card }>`
  padding: 10px;
  border-radius: 10px;
  background: ${props => props.styles.bg};
  border: 2px solid ${props => props.styles.border};
  color: ${props => props.styles.text};
  min-width: 160px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  /* 완료된 경우 약간 불투명하게 처리하여 배경감을 더 살림 */
  ${props => props.data?.completed && `
    filter: saturate(0.8);
  `}
`;
const StatusBadge = styled.div<{ color: string }>`
  font-size: 0.7rem; background: ${props => props.color}; color: #000;
  border-radius: 10px; padding: 2px 8px; display: inline-block; margin-bottom: 5px; font-weight: bold;
`;

// --- ✨ [신규] 삭제 버튼이 달린 커스텀 엣지 컴포넌트 ---
const ButtonEdge = ({
                        id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition,
                        style = {}, markerEnd, data, selected
                    }: EdgeProps) => {
    const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX, sourceY, sourcePosition, targetPosition, targetX, targetY,
    });

    return (
        <>
            <BaseEdge
                path={edgePath}
                markerEnd={markerEnd}
                style={{
                    ...style,
                    strokeWidth: selected ? 3 : 2,
                    stroke: selected ? 'var(--primary-accent)' : style.stroke
                }}
            />
            {/* ✨ 선택되었을 때만 삭제 버튼 렌더링 */}
            {selected && data?.canDelete &&(
                <EdgeLabelRenderer>
                    <div
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                            fontSize: 12,
                            pointerEvents: 'all',
                        }}
                        className="nodrag nopan"
                    >
                        <button
                            style={{
                                width: '22px', height: '22px', background: '#ff5f52', color: 'white',
                                border: '2px solid white', cursor: 'pointer', borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (window.confirm('이 연결을 끊으시겠습니까?')) {
                                    if (data?.onDelete) data.onDelete(id);
                                }
                            }}
                        >
                            ×
                        </button>
                    </div>
                </EdgeLabelRenderer>
            )}
        </>
    );
};

// ✨ edgeTypes 등록
const edgeTypes = {
    buttonEdge: ButtonEdge,
};

// ✨ [수정] 노드 컴포넌트: Completed와 State 분리 표시
const CustomCardNode = ({ data }: { data: ProjectCardNodeData }) => {
    // 프로젝트 정보(멤버 리스트)를 data와 함께 넘겨받거나 로직에서 판단
    const isMember = data.projectMembers.includes(data.userId as string) || data.projectOwner === data.userId;
    const isOrphaned = !isMember; // 멤버도 오너도 아니면 '이탈자 업무'

    const styles = getStatusStyles(data.completed, data.state, isOrphaned);

    return (
        <CardNodeWrapper styles={styles} data={data}>
            <Handle
                type="target"
                position={Position.Left}
                style={{ background: styles.border, width: '8px', height: '8px' }}
            />

            {/* 상단 뱃지 */}
            {data.completed &&
                <StatusBadge color={styles.badgeBg} style={{ color: styles.badgeText }}>
                    완료됨
                </StatusBadge>
            }

            {/* 업무 정보 */}
            <div style={{ padding: '4px 0' }}>
                {data.taskCode && (
                    <div style={{
                        fontSize: '0.75em',
                        color: data.completed ? '#81C784' : 'var(--primary-accent)',
                        fontWeight: 'bold',
                        marginBottom: '2px'
                    }}>
                        {data.taskCode}
                    </div>
                )}
                <div style={{
                    fontSize: '0.95rem',
                    fontWeight: 'bold',
                    textDecoration: data.completed ? 'line-through' : 'none', // 완료 시 취소선 추가 가능
                    opacity: data.completed ? 0.8 : 1
                }}>
                    {data.title}
                </div>
            </div>

            {/* 하단 상태 라벨 */}
            <div style={{
                fontSize: '0.75em',
                color: styles.badgeBg,
                borderTop: `1px solid ${data.completed ? 'rgba(255,255,255,0.1)' : 'var(--border-color)'}`,
                marginTop: '8px',
                paddingTop: '6px'
            }}>
                상태: <span style={{ fontWeight: 'bold' }}>{getStateLabel(data.state)}</span>
            </div>

            {isOrphaned && <StatusBadge color={styles.badgeBg}>⚠️ 담당자 부재</StatusBadge>}

            <Handle
                type="source"
                position={Position.Right}
                style={{ background: styles.border, width: '8px', height: '8px' }}
            />
        </CardNodeWrapper>
    );
};
const getStatusStyles = (completed?: boolean | null, state?: string | null, isOrphaned?: boolean) => {
    // ✨ [신규] 이탈자 업무인 경우 최우선으로 시뻘건 경고 스타일 적용
    if (isOrphaned) {
        return {
            border: '#ff5f52',
            bg: '#4a1111', // 어두운 빨강 배경
            text: '#ffcdd2',
            badgeBg: '#ff5f52',
            badgeText: '#fff'
        };
    }

    // 1. 실질적 완료 (Check 완료) - 노드 배경을 채움
    if (completed) {
        return {
            border: '#81C784',
            bg: '#1b3a24', // 차분하고 깊은 숲색 (눈 안 아프고 확연히 구분됨)
            text: '#e0e0e0',
            badgeBg: '#81C784',
            badgeText: '#000'
        };
    }

    // 2. 미완료 상태별 (테두리 위주)
    switch (state) {
        case 'PROCESS': // 진행
            return { border: '#64B5F6', bg: 'var(--bg-card)', text: 'var(--text-bright)', badgeBg: '#64B5F6', badgeText: '#000' };
        case 'FOLLOWUP': // 팔로업
            return { border: '#BA68C8', bg: 'var(--bg-card)', text: 'var(--text-bright)', badgeBg: '#BA68C8', badgeText: '#000' };
        case 'IDLE': // 대기
            return { border: '#FFD54F', bg: 'var(--bg-card)', text: 'var(--text-bright)', badgeBg: '#FFD54F', badgeText: '#000' };
        case 'COMPLETED': // 상태만 완료 (체크 전)
            return { border: '#4DB6AC', bg: 'var(--bg-card)', text: 'var(--text-bright)', badgeBg: '#4DB6AC', badgeText: '#000' };
        default: // 기본
            return { border: '#959DA5', bg: 'var(--bg-card)', text: 'var(--text-normal)', badgeBg: '#959DA5', badgeText: '#000' };
    }
};
// ✨ [수정] 상태 색상 로직 분리
// const getStatusColor = (completed?: boolean | null, state?: string | null) => {
//     // 1. 실질적 완료 (Check 완료) - 가장 강력한 상태
//     if (completed) return '#81C784'; // Green
//
//     // 2. 진행 상태 (State)
//     switch (state) {
//         case 'IDLE': return '#FFD54F'; // Yellow
//         case 'FOLLOWUP': return '#BA68C8'; // Purple
//         case 'PROCESS': return '#64B5F6'; // Blue
//         case 'COMPLETED': return '#4DB6AC'; // Teal (완료 상태지만 체크 안됨 - 구분)
//         default: return '#959DA5'; // Grey
//     }
// };
const getStateLabel = (state?: string | null) => {
    switch (state) {
        case 'IDLE': return '대기';
        case 'PROCESS': return '진행';
        case 'FOLLOWUP': return '팔로업';
        case 'COMPLETED': return '완료';
        default: return '미지정';
    }
};
const nodeTypes = { cardNode: CustomCardNode };

const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    const nodeWidth = 180;
    const nodeHeight = 100;
    // dagreGraph.setGraph({ rankdir: 'TB' });
    dagreGraph.setGraph({ rankdir: 'LR', nodesep: 50, ranksep: 100 });
    nodes.forEach((node) => dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight }));
    edges.forEach((edge) => dagreGraph.setEdge(edge.source, edge.target));
    dagre.layout(dagreGraph);
    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            // position: { x: nodeWithPosition.x - nodeWidth / 2, y: nodeWithPosition.y - nodeHeight / 2 },
            position: {
                x: nodeWithPosition.x - nodeWidth / 2,
                y: nodeWithPosition.y - nodeHeight / 2
            },
        };
    });
    return { nodes: layoutedNodes, edges };
};

const ProjectDetailPageContent = () => {
    const user = useAuth();
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const [project, setProject] = useState<Project | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [editTitle, setEditTitle] = useState('');
    const [editDesc, setEditDesc] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [availableCards, setAvailableCards] = useState<Card[]>([]);
    const [selectedCardIdsToAdd, setSelectedCardIdsToAdd] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const { getNodes, getEdges } = useReactFlow();

    const isAllTasksCompleted = nodes.length > 0 && nodes.every(n => (n.data as Card).completed);
    const isProjectCompleted = project?.status === 'COMPLETED';

    const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
    const [memberSearchTerm, setMemberSearchTerm] = useState('');
    const [memberSearchResults, setMemberSearchResults] = useState<User[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null);
    const [connectSourceId, setConnectSourceId] = useState<string | null>(null);

    // const canEditEdges = project?.ownerId === user?.id || project?.editorIds?.includes(user?.id as string);

// 1. deleteSpecificEdge의 최신 참조를 보관할 Ref 생성
    const deleteEdgeRef = useRef<(id: string) => Promise<void>>(undefined);

// 2. 가장 먼저 fetchProjectData 정의 (다른 모든 로직의 기초)
    const fetchProjectData = useCallback(async (id: string) => {
        try {
            const pRes = await getProjectById(id);
            if (!pRes.data) {
                alert("존재하지 않는 프로젝트입니다.");
                navigate('/projects');
                return;
            }
            const projectInfo = pRes.data; //
            setProject(projectInfo);
            setEditTitle(projectInfo.title);
            setEditDesc(projectInfo.description);

            const cRes = await getProjectCards(id);
            const cards = cRes.data;

            const flowNodes: Node[] = cards.map(card => ({
                id: card.id,
                type: 'cardNode',
                data: {
                    ...card,
                    projectMembers: projectInfo.memberIds || [], //
                    projectOwner: projectInfo.ownerId
                } as ProjectCardNodeData,
                position: { x: 0, y: 0 }
            }));

            const flowEdges: Edge[] = [];
            cards.forEach(card => {
                const preds = card.predecessorIds || []; //
                preds.forEach((pid: string) => {
                    flowEdges.push({
                        id: `e-${pid}-${card.id}`,
                        source: pid,
                        target: card.id,
                        type: 'buttonEdge',
                        markerEnd: { type: MarkerType.ArrowClosed },
                        animated: true,
                        data: {
                            // ✨ Ref를 통해 실행 시점에 최신 함수를 호출 (의존성 배열 무관)
                            onDelete: (edgeId: string) => deleteEdgeRef.current?.(edgeId),
                            canDelete: projectInfo.ownerId === user?.id || projectInfo.editorIds?.includes(user?.id as string)
                        }
                    });
                });
            });

            const layouted = getLayoutedElements(flowNodes, flowEdges);
            setNodes(layouted.nodes);
            setEdges(layouted.edges);
        } catch (e) {
            console.error("Failed to load project", e);
            navigate('/projects');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, user?.id, setNodes, setEdges]); // deleteSpecificEdge를 여기서 제외함

// 3. deleteSpecificEdge 정의
    const deleteSpecificEdge = useCallback(async (edgeId: string) => {
        const currentEdges = getEdges();
        const currentNodes = getNodes();

        const edgeToDelete = currentEdges.find(e => e.id === edgeId);
        if (!edgeToDelete) return;

        const targetId = edgeToDelete.target;
        const sourceId = edgeToDelete.source;
        const targetNode = currentNodes.find(n => n.id === targetId);
        if (!targetNode) return;

        const currentPreds = (targetNode.data as Card).predecessorIds || [];
        const newPreds = currentPreds.filter((id: string) => id !== sourceId);

        try {
            // 사용자님의 API 규격: updateCardEdges({ id, predecessorIds })
            await updateCardEdges({ id: targetId, predecessorIds: newPreds });

            setEdges((eds) => eds.filter((e) => e.id !== edgeId));
            setNodes(nds => nds.map(n =>
                n.id === targetId ? { ...n, data: { ...n.data, predecessorIds: newPreds } } : n
            ));
        } catch (e: any) {
            alert("연결 해제 실패 [" + (e.response?.data?.message || "권한 부족") + "]");
            if (projectId) fetchProjectData(projectId); // 롤백
        }
    }, [getEdges, getNodes, setEdges, setNodes, projectId, fetchProjectData]);

// 4. ✨ 중요: deleteSpecificEdge가 정의된 후 Ref에 할당
    useEffect(() => {
        deleteEdgeRef.current = deleteSpecificEdge;
    }, [deleteSpecificEdge]);

// 3. 통합된 간선 연결 로직 (onConnect)
    const onConnect = useCallback(async (params: Connection) => {
        if (!params.source || !params.target) return;

        const currentNodes = getNodes();
        const targetNode = currentNodes.find(n => n.id === params.target);
        if (!targetNode) return;

        const currentCard = targetNode.data as Card;
        const currentPreds = currentCard.predecessorIds || [];

        if (currentPreds.includes(params.source)) return;

        try {
            const newPreds = [...currentPreds, params.source];

            // ✨ [통합 API 호출] CardDto.Modify 규격 사용
            await updateCardEdges({ id: params.target, predecessorIds: newPreds } as any);

            // 선 그리기 및 노드 데이터 갱신
            setEdges((eds) => addEdge({
                ...params,
                markerEnd: { type: MarkerType.ArrowClosed },
                animated: true,
                type: 'buttonEdge',
                data: { onDelete: deleteSpecificEdge }
            }, eds));

            setNodes(nds => nds.map(n => {
                if (n.id === params.target) {
                    return { ...n, data: { ...n.data, predecessorIds: newPreds }};
                }
                return n;
            }));
        } catch (e: any) {
            alert("연결 실패 [" + (e.response?.data?.message || "권한 부족") + "]");
            if (projectId) fetchProjectData(projectId);
        }
    }, [getNodes, projectId, setEdges, setNodes, fetchProjectData, deleteSpecificEdge]);

    // 공통 간선 업데이트 함수
    // const handleEdgeUpdate = async (targetCardId: string, nextPredecessorIds: string[]) => {
    //     try {
    //         // 기존에 분리되어 있던 API들을 이 하나로 대체합니다.
    //         await updateCardEdges(targetCardId, nextPredecessorIds);
    //
    //         // 데이터 새로고침 및 UI 상태 동기화
    //         fetchProjectData(projectId!);
    //     } catch (e: any) {
    //         alert("간선 수정 실패: " + (e.response?.data?.message || "권한이 없습니다."));
    //         fetchProjectData(projectId!); // 실패 시 UI 롤백을 위해 새로고침
    //     }
    // };

    // 멤버 권한(편집자) 토글 핸들러
    const handleToggleEditor = async (targetUserId: string) => {
        if (!project || !user || project.ownerId !== user.id) return;

        let nextEditors = [...(project.editorIds || [])];
        if (nextEditors.includes(targetUserId)) {
            nextEditors = nextEditors.filter(id => id !== targetUserId); // 해제
        } else {
            nextEditors.push(targetUserId); // 부여
        }

        try {
            // 오직 오너만 이 API를 성공적으로 호출할 수 있습니다.
            await updateProject({ id: project.id, editorIds: nextEditors } as any);
            setProject({ ...project, editorIds: nextEditors });
        } catch (e) {
            alert("권한 수정 실패");
        }
    };

    // 멤버 검색 핸들러
    const handleSearchMember = async () => {
        if (!memberSearchTerm.trim()) return;

        setIsSearching(true);
        try {
            // searchUsers API 호출 (api.ts에 등록 필요)
            const res = await searchUsers(memberSearchTerm);
            setMemberSearchResults(res.data);
        } catch (e) {
            alert("검색 중 오류가 발생했습니다.");
        } finally {
            setIsSearching(false);
        }
    };

    // 멤버 관리 모달 열기
    const handleOpenMemberModal = async () => {
        try {
            // const res = await getAllUsers(); // API 구현 필요
            // setAllUsers(res.data);
            setIsMemberModalOpen(true);
        } catch (e) { alert("사용자 목록을 불러오지 못했습니다."); }
    };

    // 멤버 추가/삭제 통합 처리
    const handleUpdateMembers = async (targetUserId: string, isAdding: boolean) => {
        if (!project || !user) return;

        if (project.ownerId === user.id) {
            let nextMembers = [...(project.memberIds || [])];
            if (isAdding) {
                if (!nextMembers.includes(targetUserId)) nextMembers.push(targetUserId);
            } else {
                nextMembers = nextMembers.filter(id => id !== targetUserId);
            }

            try {
                await updateProject({id: project.id, memberIds: nextMembers} as any);
                setProject({...project, memberIds: nextMembers});
            } catch (e) {
                alert("멤버 업데이트 실패");
            }
        } else {
            alert("소유자만 추가할 수 있습니다.");
        }
    };

    // ✨ [신규] 프로젝트 상태 토글 핸들러
    const handleToggleProjectStatus = async () => {
        if (!project) return;

        // Enum 값 사용 ('ACTIVE' <-> 'COMPLETED')
        const newStatus: ProjectStatus = isProjectCompleted ? 'ACTIVE' : 'COMPLETED';
        const confirmMsg = isProjectCompleted
            ? "프로젝트를 다시 진행 상태로 변경하시겠습니까?"
            : "프로젝트를 종료하시겠습니까? (목록에서 숨겨집니다)";

        if (window.confirm(confirmMsg)) {
            try {
                // updateProject API가 ProjectDto.Modify 구조를 받으므로 status 필드만 보내도 됨
                await updateProject({ id: project.id, status: newStatus } as any);
                setProject({ ...project, status: newStatus });

                alert(`프로젝트가 ${newStatus === 'COMPLETED' ? '종료' : '재개'}되었습니다.`);
                if (newStatus === 'COMPLETED') navigate('/projects'); // 종료 시 목록으로 이동
            } catch (e) {
                alert("상태 변경 실패");
            }
        }
    };

    // ✨ [신규] 검색 로직 (모달이 열릴 때 초기화는 handleOpenAddModal에서 함)
    const filteredAvailableCards = availableCards.filter(card =>
        card.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (card.taskCode && card.taskCode.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    useEffect(() => {
        if (projectId) {
            fetchProjectData(projectId);
        }
    }, [projectId, fetchProjectData]);

    const handleNodeClick = (event: React.MouseEvent, node: Node) => {
        // 1. 만약 지금 "연결하기" 모드라면?
        if (connectSourceId) {
            if (connectSourceId === node.id) {
                setConnectSourceId(null); // 자기 자신 클릭 시 취소
                return;
            }
            // 실제로 연결 실행
            // sourceHandle과 targetHandle에 null을 명시하여 Connection 타입을 충족시킵니다.
            onConnect({
                source: connectSourceId,
                target: node.id,
                sourceHandle: null,
                targetHandle: null
            });
            setConnectSourceId(null);
            return;
        }

        // 2. 연결 모드가 아니라면 메뉴를 띄움
        setMenu({
            id: node.id,
            x: event.clientX,
            y: event.clientY
        });
    };

    // 배경 클릭 시 메뉴 닫기
    const onPaneClick = () => {
        setMenu(null);
        setConnectSourceId(null);
    };

    // 상세 보기 액션
    const handleViewDetail = (nodeId: string) => {
        const node = nodes.find(n => n.id === nodeId);
        const cardData = node?.data as Card;
        const targetId = cardData.taskCode || cardData.id;
        navigate(`/projects/${projectId}/card/${targetId}${location.search}`);
        setMenu(null);
    };

// 연결 시작 액션
    const handleStartConnect = (nodeId: string) => {
        setConnectSourceId(nodeId);
        setMenu(null);
        alert("연결할 대상 업무를 선택해주세요."); // 사용자 가이드
    };

    const handleRelayout = useCallback(() => {
        const layouted = getLayoutedElements(nodes, edges);
        setNodes([...layouted.nodes]);
        setEdges([...layouted.edges]);
    }, [nodes, edges, setNodes, setEdges]);

    const onEdgesDelete = useCallback(async (deletedEdges: Edge[]) => {
        for (const edge of deletedEdges) {
            const sourceId = edge.source;
            const targetId = edge.target;
            const targetNode = nodes.find(n => n.id === targetId);
            if (!targetNode) continue;

            const currentCard = targetNode.data as Card;
            const currentPreds = currentCard.predecessorIds || [];
            const newPreds = currentPreds.filter(id => id !== sourceId);

            try {
                await updateCardEdges({ id: targetId, predecessorIds: newPreds } as any);
                setNodes(nds => nds.map(n => {
                    if (n.id === targetId) {
                        return { ...n, data: { ...n.data, predecessorIds: newPreds } };
                    }
                    return n;
                }));
            } catch (e) {
                console.error("Failed to remove connection", e);
                alert("연결 해제 실패");
            }
        }
    }, [nodes, setNodes]);

    const handleOpenAddModal = async () => {
        try {
            setSearchQuery('');
            const res = await getMyIdleCards();
            const idleCards = (res.data._embedded?.getList || []).filter(c => !c.projectId);
            setAvailableCards(idleCards);
            setSelectedCardIdsToAdd(new Set());
            setIsAddModalOpen(true);
        } catch (e) { console.error(e); }
    };

    const handleAddCardsToProject = async () => {
        if(!projectId) return;
        try {
            await assignCardsToProject(projectId, Array.from(selectedCardIdsToAdd));
            setIsAddModalOpen(false);
            fetchProjectData(projectId);
        } catch (e) {
            alert("추가 실패: " + e);
        }
    };

    const handleProjectUpdate = async () => {
        if(!project) return;
        try {
            await updateProject({ id: project.id, title: editTitle, description: editDesc });
            alert("저장되었습니다.");
            fetchProjectData(project.id);
        } catch(e) { alert("수정 실패"); }
    };

    const handleDeleteProject = async () => {
        if(!project) return;
        if(window.confirm("정말로 이 프로젝트를 삭제하시겠습니까?")) {
            await deleteProject(project.id);
            navigate('/projects');
        }
    };

    const projectTitle = isProjectCompleted ? `[종료됨] ${project?.title}` : project?.title;

    // ✨ 프로젝트 데이터가 없을 때는 깡통 화면을 그리지 않고 로딩 또는 빈 화면 처리
    if (!project) return null;

    return (
        <Container>
            <ResponsiveHeader
                title={`Project: ${projectTitle}`}
                extraActions={
                    <>
                        <Button onClick={handleOpenAddModal} cyan>+ Add Tasks</Button>
                        <Button onClick={handleRelayout}>↺ Re-Layout</Button>
                    </>
                }
            />

            <DiagramArea>
                <SidebarContainer isOpen={sidebarOpen}>
                    <SidebarContent>
                        <SidebarHeader>
                            <h2>프로젝트 정보</h2>
                        </SidebarHeader>

                        {/* ✨ [신규] 완료 안내 메시지 */}
                        {isAllTasksCompleted && !isProjectCompleted && (
                            <div style={{
                                padding: '1rem',
                                backgroundColor: 'rgba(129, 199, 132, 0.2)',
                                border: '1px solid #81C784',
                                borderRadius: '8px',
                                color: '#81C784',
                                marginBottom: '1.5rem',
                                fontSize: '0.9rem',
                                lineHeight: '1.4'
                            }}>
                                🎉 <b>모든 업무가 완료되었습니다!</b><br/>
                                프로젝트를 종료할 준비가 되었습니다.
                            </div>
                        )}

                        {/* ✨ [수정] 프로젝트 종료/재개 버튼: 소유자만 가능 */}
                        {project.ownerId === user?.id && (
                            <Button
                                fullWidth
                                onClick={handleToggleProjectStatus}
                                style={{
                                    marginBottom: '1rem',
                                    backgroundColor: isProjectCompleted ? '#64B5F6' : '#81C784',
                                    color: 'white'
                                }}
                            >
                                {isProjectCompleted ? '프로젝트 다시 열기' : '프로젝트 종료'}
                            </Button>
                        )}

                        <InputGroup>
                            <Label>제목</Label>
                            {/* 본인이 주인이 아니면 읽기 전용 처리 */}
                            <StyledInput
                                value={editTitle}
                                onChange={e => setEditTitle(e.target.value)}
                                readOnly={project.ownerId !== user?.id}
                            />
                        </InputGroup>
                        <InputGroup>
                            <Label>상세 설명</Label>
                            {/* 본인이 주인이 아니면 읽기 전용 처리 */}
                            <StyledTextarea
                                value={editDesc}
                                onChange={e => setEditDesc(e.target.value)}
                                readOnly={project.ownerId !== user?.id}
                            />
                        </InputGroup>
                        {/* ✨ [수정] 저장 및 삭제 버튼: 소유자만 보이도록 조건부 렌더링 */}
                        {project.ownerId === user?.id && !isProjectCompleted && (
                            <>
                                <Button cyan fullWidth onClick={handleProjectUpdate} style={{ marginBottom: '1rem' }}>저장</Button>
                                <Button fullWidth onClick={handleDeleteProject} style={{ background: 'var(--danger-accent)', color: 'white' }}>삭제</Button>
                            </>
                        )}

                        &nbsp;

                        {/* 사이드바 내 정보 섹션 아래 추가 */}
                        <InputGroup>
                            <Label>참여 멤버 ({project.memberIds?.length || 0}명)</Label>
                            <MemberList>
                                {project.memberIds?.map(mId => (
                                    <MemberItem key={mId}>
                                        <span style={{fontSize: '0.9rem'}}>{mId}</span>
                                        {/* 소유자만 멤버 내보내기 가능 */}
                                        {project.ownerId === user?.id && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button
                                                    onClick={() => handleToggleEditor(mId)}
                                                    style={{ fontSize: '0.7rem', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-normal)' }}
                                                >
                                                    {project.editorIds?.includes(mId) ? '권한 박탈' : '편집자 임명'}
                                                </button>
                                                <button
                                                    onClick={() => handleUpdateMembers(mId, false)}
                                                    style={{background: 'none', border: 'none', color: '#ff5f52', cursor: 'pointer'}}
                                                >제거</button>
                                            </div>
                                        )}
                                    </MemberItem>
                                ))}
                            </MemberList>
                            {project.ownerId === user?.id && (
                                <Button onClick={handleOpenMemberModal} fullWidth style={{marginTop: '0.5rem'}}>+ 멤버 초대</Button>
                            )}
                        </InputGroup>
                    </SidebarContent>
                </SidebarContainer>

                <div style={{ flex: 1, position: 'relative', touchAction: 'none' }}>
                    <SidebarToggleBtn onClick={() => setSidebarOpen(!sidebarOpen)}>
                        {sidebarOpen ? '◀ 숨기기' : '▶ 정보 보기'}
                    </SidebarToggleBtn>

                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onPaneClick={onPaneClick}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onNodeClick={handleNodeClick}
                        onConnect={onConnect}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onEdgesDelete={onEdgesDelete}
                        fitView
                        // 모바일에서 기본적으로 줌/팬이 가능하도록 설정 (기본값이긴 하나 명시)
                        panOnDrag={true}
                        zoomOnPinch={true}
                        zoomOnDoubleClick={true}
                    >
                        <Background />
                        <Controls />
                        <MiniMap />
                    </ReactFlow>

                    {/* ✨ 커스텀 컨텍스트 메뉴 */}
                    {menu && (
                        <ContextMenu x={menu.x} y={menu.y}>
                            <MenuButton onClick={() => handleViewDetail(menu.id)}>🔍 상세 보기</MenuButton>
                            <MenuButton onClick={() => handleStartConnect(menu.id)}>🔗 업무 연결하기</MenuButton>
                            <MenuButton onClick={() => setMenu(null)} style={{color: 'var(--text-dark)'}}>취소</MenuButton>
                        </ContextMenu>
                    )}

                    {/* ✨ 연결 모드 알림바 */}
                    {connectSourceId && (
                        <div style={{
                            position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
                            background: 'var(--primary-accent)', color: 'white', padding: '0.5rem 1rem',
                            borderRadius: '20px', zIndex: 1000, fontWeight: 'bold'
                        }}>
                            연결 모드: 대상 업무를 선택하세요 (취소: 배경 클릭)
                        </div>
                    )}
                </div>
            </DiagramArea>

            <Routes>
                <Route path="card/:cardId" element={
                    <CardRouteWrapper
                        project={project}
                        onClose={() => navigate(`/projects/${projectId}${location.search}`)}
                        onUpdateNeeded={() => fetchProjectData(projectId!)}
                        // ✨ [신규] 프로젝트에서 제외 핸들러 전달
                        onRemoveFromProject={async (cardId) => {
                            try {
                                await unassignCardFromProject(cardId);
                                fetchProjectData(projectId!);
                                navigate(`/projects/${projectId}${location.search}`); // 모달 닫기
                            } catch (e) {
                                alert("제외 실패");
                            }
                        }}
                    />}
                />
            </Routes>

            <Modal visible={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
                <AddTaskModalContent>
                    <h2>Add Existing Tasks to Project</h2>
                    <p style={{color: 'var(--text-normal)', marginBottom: '1rem'}}>
                        프로젝트에 속하지 않은 업무만 표시됩니다.
                    </p>

                    {/* ✨ [신규] 검색창 추가 */}
                    <div style={{ marginBottom: '1rem' }}>
                        <Input
                            placeholder="업무 제목 검색..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <TaskSelectRequest>
                        {filteredAvailableCards.length === 0 ? <p>추가할 수 있는 업무가 없습니다.</p> :
                            filteredAvailableCards.map(card => (
                                <TaskItem key={card.id}>
                                    <input
                                        type="checkbox"
                                        checked={selectedCardIdsToAdd.has(card.id)}
                                        onChange={() => {
                                            const next = new Set(selectedCardIdsToAdd);
                                            if(next.has(card.id)) next.delete(card.id);
                                            else next.add(card.id);
                                            setSelectedCardIdsToAdd(next);
                                        }}
                                    />
                                    {card.taskCode && <span style={{ fontWeight: 'bold', marginRight: '0.5rem', color: 'var(--primary-accent)' }}>{card.taskCode}</span>}
                                    <span>{card.title}</span>
                                </TaskItem>
                            ))
                        }
                    </TaskSelectRequest>
                    <Button fullWidth cyan onClick={handleAddCardsToProject}>Add Selected Tasks</Button>
                </AddTaskModalContent>
            </Modal>

            {/* 멤버 초대 모달 (기존 Modal 컴포넌트 활용) */}
            <Modal visible={isMemberModalOpen} onClose={() => {
                setIsMemberModalOpen(false);
                setMemberSearchResults([]);
                setMemberSearchTerm('');
            }}>
                <div style={{padding: '1.5rem', minWidth: '350px'}}>
                    <h3 style={{marginBottom: '1rem'}}>멤버 초대</h3>

                    {/* 검색창 */}
                    <div style={{display: 'flex', gap: '0.5rem', marginBottom: '1.5rem'}}>
                        <Input
                            placeholder="아이디 또는 이름 검색"
                            value={memberSearchTerm}
                            onChange={e => setMemberSearchTerm(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearchMember()}
                        />
                        <Button onClick={handleSearchMember} cyan disabled={isSearching}>
                            {isSearching ? '...' : '검색'}
                        </Button>
                    </div>

                    {/* 검색 결과 목록 */}
                    <div style={{maxHeight: '250px', overflowY: 'auto'}}>
                        {memberSearchResults.length === 0 ? (
                            <p style={{textAlign: 'center', color: 'var(--text-dark)', fontSize: '0.9rem'}}>
                                검색 결과가 없습니다.
                            </p>
                        ) : (
                            memberSearchResults.map(user => (
                                <MemberItem key={user.id} style={{border: '1px solid var(--border-color)', padding: '0.8rem'}}>
                                    <div>
                                        <div style={{fontWeight: 'bold', color: 'var(--text-bright)'}}>{user.name}</div>
                                        <div style={{fontSize: '0.8rem', color: 'var(--text-dark)'}}>@{user.id}</div>
                                    </div>
                                    {project?.memberIds?.includes(user.id) || project?.ownerId === user.id ? (
                                        <span style={{fontSize: '0.8rem', color: 'var(--primary-accent)'}}>참여 중</span>
                                    ) : (
                                        <Button onClick={() => handleUpdateMembers(user.id, true)}>초대</Button>
                                    )}
                                </MemberItem>
                            ))
                        )}
                    </div>
                </div>
            </Modal>
        </Container>
    );
};

const CardRouteWrapper: React.FC<{ project: any, onClose: () => void, onUpdateNeeded: () => void, onRemoveFromProject: (id: string) => void }> = ({ project, onClose, onUpdateNeeded, onRemoveFromProject }) => {
    const { cardId } = useParams<{ cardId: string }>();
    const [card, setCard] = useState<Card | null>(null);

    const user = useAuth();
    const isReadOnly = card?.userId !== user?.id && project?.ownerId !== user?.id && card?.assigneeId !== user?.id;

    const isAdminEdit = card?.userId !== user?.id && project?.ownerId === user?.id;

    useEffect(() => {
        const fetchCard = async () => {
            if (cardId) {
                try {
                    const response = await getCardById(cardId);
                    setCard(response.data);
                } catch (error) {
                    console.error("Failed to fetch card", error);
                    onClose();
                }
            }
        };
        fetchCard();
    }, [cardId, onClose]);

    if (!card || !user) return null;

    const handleToggleComplete = async () => {
        const originalCard = { ...card };
        try {
            await updateCard({ id: card.id, completed: !card.completed });
            onUpdateNeeded();
        } catch (error: any) {
            console.error("Failed to toggle complete", error);
            setCard(originalCard);
            // 백엔드에서 보낸 에러 메시지 표시
            const message = error.response?.data?.data || "완료 처리 실패";
            alert(message);
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Delete this card?')) {
            await deleteCard(card.id);
            onUpdateNeeded();
            onClose();
        }
    };

    return (
        <CardDetailModal
            card={card}
            onClose={onClose}
            onUpdate={(updatedCard) => {
                setCard(updatedCard);
                onUpdateNeeded();
            }}
            onDelete={handleDelete}
            onToggleComplete={handleToggleComplete}
            readOnly={isReadOnly}
            isAdminEdit={isAdminEdit} // ✨ 플래그 전달
            onRemoveFromProject={() => onRemoveFromProject(card!.id)}
        />
    );
};

const ProjectDetailPage = () => (
    <ReactFlowProvider>
        <ProjectDetailPageContent />
    </ReactFlowProvider>
);

export default ProjectDetailPage;